import { useState } from "react";
import { useNavigate } from "react-router";

import { useProjectsQuery } from "@/api/hooks";
import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import { queryKeys } from "@/api/hooks";
import { formatDate } from "@/lib/format";
import { Button } from "@/ui/Button";
import { EmptyState } from "@/ui/states";

import { useProjectId } from "./ProjectLayoutRoute";

/**
 * Create from — the canonical one-time-copy surface (#518).
 *
 * `/projects/:projectId/create-from` renders IN the project frame — the
 * canonical authority shows it as a full page under the project's header and
 * tabs, not as a modal — offering the other projects this caller can open as
 * source cards, one selection, Cancel, and a Create-draft action.
 *
 * WHAT THE ACTION DOES TODAY, exactly as the canonical authority does it: the
 * canonical surface carries the stub note "Copying is stubbed — selecting a
 * source will just seed the draft" and its Create-draft control returns to
 * the Overview without copying anything. That note was verified still present
 * at implementation time (2026-08-26), so this surface is the same honest
 * stub: the real backend-driven copy/import flow is #352's, and wiring a
 * different behaviour here would be inventing product the canonical does not
 * have. The card content renders the fields the Editor Service actually
 * serves — name, preview target, creation date — never fabricated version or
 * content counts.
 *
 * ONE-TIME is the point of the copy model (`CloneProject`'s contract): a copy
 * is a starting snapshot with no ongoing link, which is what the description
 * sentence states.
 */
export function ProjectCreateFromRoute() {
  const projectId = useProjectId();
  const navigate = useNavigate();
  const projects = useProjectsQuery("");
  const [sourceId, setSourceId] = useState<string | null>(null);

  function toOverview() {
    void navigate(`/projects/${encodeURIComponent(projectId)}`);
  }

  if (projects.isPending) {
    return (
      <QueryPendingState
        isPaused={projects.isPaused}
        failureReason={projects.failureReason}
        label="Loading projects to copy from"
        errorTitle="The projects you can copy from could not be loaded"
        rows={2}
        queryKey={queryKeys.projects("")}
      />
    );
  }

  if (!projects.data) {
    return (
      <QueryErrorState
        error={projects.error}
        subject="Projects"
        title="The projects you can copy from could not be loaded"
        deniedDescription="The projects you can copy from are not available to your account in this environment."
        onRetry={() => void projects.refetch()}
      />
    );
  }

  const sources = projects.data.items.filter((item) => item.id !== projectId);
  const selected = sources.find((item) => item.id === sourceId) ?? null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">Create from</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Take a one-time starting snapshot of another project&apos;s
          configuration. No ongoing link — later changes there won&apos;t flow
          in.
        </p>
      </div>

      <p className="inline-flex items-center gap-2 rounded-md border border-dashed border-warning-foreground/30 bg-warning/40 px-3 py-1.5 text-xs text-warning-foreground">
        Copying is stubbed — selecting a source will just seed the draft.
      </p>

      {sources.length === 0 ? (
        <EmptyState
          title="No projects to copy from"
          description="No other project is available to your account in this environment, so there is nothing to take a starting snapshot of."
        />
      ) : (
        <div className="grid max-w-3xl grid-cols-1 gap-3 sm:grid-cols-2">
          {sources.map((source) => (
            <button
              key={source.id}
              type="button"
              aria-pressed={sourceId === source.id}
              onClick={() => setSourceId(source.id)}
              className={`rounded-lg border p-4 text-left transition hover:border-primary ${
                sourceId === source.id
                  ? "border-primary bg-accent/30"
                  : "border-border bg-card"
              }`}
            >
              <span className="block font-medium">{source.name}</span>
              <span className="mt-1 block text-xs text-muted-foreground">
                {source.previewTenantId}
              </span>
              <span className="mt-3 block text-xs text-muted-foreground">
                {`Last activity ${formatDate(source.lastMeaningfulActivityAt)}`}
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <Button variant="outline" onClick={toOverview}>
          Cancel
        </Button>
        <Button
          disabled={selected === null || sources.length === 0}
          onClick={toOverview}
          aria-describedby={selected === null ? "create-from-reason" : undefined}
        >
          {selected === null
            ? "Create draft"
            : `Create draft from ${selected.name}`}
        </Button>
      </div>
      {selected === null && sources.length > 0 ? (
        <p id="create-from-reason" className="text-xs text-muted-foreground">
          Choose the project to copy from.
        </p>
      ) : null}
    </div>
  );
}
